import React, { useState, useEffect, MouseEventHandler } from 'react';
import { get, orderBy } from 'lodash';
import { MenuItem, Select, FormControl, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useSelector } from 'react-redux';
import { injectIntl, IntlShape } from 'react-intl';
import { Autocomplete } from '@material-ui/lab';
import { filterStackHolder } from 'services/utils';
import {
  Container,
  RefreshButton,
  SearchButton,
  StyledInputBase,
} from 'components/NewSearchBar/style';
import messages from 'containers/IntervenantManagement/messages';
import { translate } from 'containers/services/translate';
import { COLORS } from 'constants/design';
import { SEARCH_TYPE_IDS } from 'containers/IntervenantManagement/constants';
import { LISTINGS } from 'containers/data-structure';
import { ITEM_KEYS, SAGIS_KEYS } from 'containers/RecapManagerPage/constants';
import * as RecapUtils from 'containers/RecapManagerPage/utils';

export interface SearchQuery {
  lawyerCode: string | null;
  jurisdictionId: string | null;
}
export interface SearchBarProps {
  intl: IntlShape;
  searchQuery: any;
  setSearchQuery: Function;
  handlerSearch: Function;
  handlerRefresh: MouseEventHandler;
  listings: any;
  lawyers: any;
}

const SearchBar: React.FC<SearchBarProps> = (props): JSX.Element => {
  const { handlerSearch, handlerRefresh, searchQuery, setSearchQuery } = props;
  const [assignedLawyers, setAssignedLawyers] = React.useState<any>([]);
  const [searchType, setSearchType] = useState([] as any);
  const [stakeHolders, setStakeholders] = React.useState([]);

  const [selectedSearchType, setSelectedSearchType] = useState(
    SEARCH_TYPE_IDS.JURISDICTION,
  );

  const handleChangeSelectedSearchType = (
    e: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setSelectedSearchType(e.target.value as string);
  };

  useEffect(() => {
    setSearchType(allSearchTypes['DEFAULT']);
    const _stackholders = RecapUtils.getListBySagisKey(
      get(props.listings, LISTINGS.STACKHOLDER_TYPE, []),
      get(props.listings, LISTINGS.STACKHOLDERS, []),
      ITEM_KEYS.LAWYER,
      SAGIS_KEYS.ITEM_TYPE as any,
    );

    const formattedLawyers = RecapUtils.getBawabaLawyers(
      _stackholders,
      props.lawyers,
    );
    setStakeholders(formattedLawyers as any);
  }, [props.listings, props.lawyers]);

  const allSearchTypes = {
    DEFAULT: [
      {
        id: SEARCH_TYPE_IDS.JURISDICTION,
        title: translate(messages.jurisdictionTitle, props),
      },
      {
        id: SEARCH_TYPE_IDS.LAWYER,
        title: translate(messages.lawyerTitle, props),
      },
    ],
  };

  const assignValuesToSearchQueryBeforeSearch = (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
    newValue: any | null,
  ) => {
    searchQuery.jurisdictionId = null;
    searchQuery.lawyerCodes = get(newValue, 'ItemKey', null);
    setAssignedLawyers(newValue);
    setSearchQuery({
      ...searchQuery,
      lawyerCodes: [get(newValue, 'ItemKey', null)],
    });
  };

  const juridictionListing = useSelector((state: any) =>
    get(state.toJS(), ['appContainer', 'listings', 'jurisdiction']),
  );
  const handleChangeJuridictionValue = (
    e: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setSearchQuery({
      ...searchQuery,
      lawyerCodes: [],
      jurisdictionId: get(
        juridictionListing.find((pro: any) => pro.id === e.target.value),
        'id',
      ),
    });
  };
  const handleSearch = () => {
    handlerSearch(searchQuery);
  };

  return (
    <Container>
      <Select
        value={selectedSearchType}
        onChange={handleChangeSelectedSearchType}
        input={
          <StyledInputBase borderRadiusLeft fullWidth textColor="black" bold />
        }
        data-testid="search-type"
      >
        {searchType.map((searchType: any) => (
          <MenuItem value={searchType.id}>{searchType.title}</MenuItem>
        ))}
      </Select>
      {selectedSearchType === SEARCH_TYPE_IDS.JURISDICTION ? (
        <Select
          onChange={handleChangeJuridictionValue}
          input={
            <StyledInputBase marginRight fullWidth textColor="black" bold />
          }
          value={searchQuery?.jurisdictionId}
          defaultValue={0}
        >
          {get(props.listings, 'jurisdiction', []) &&
            orderBy(
              get(props.listings, 'jurisdiction', []),
              [el => el.title.toLowerCase()],
              ['asc'],
            ).map((procedure: any) => (
              <MenuItem value={procedure.id}>{procedure.title}</MenuItem>
            ))}
        </Select>
      ) : (
        <FormControl fullWidth>
          <Autocomplete
            className="lawyer-item"
            disabled={false}
            id="assignedLawyers"
            options={filterStackHolder(stakeHolders)}
            getOptionLabel={(option: any) =>
              `${option?.ItemDescription ? option?.ItemDescription : ''}`
            }
            fullWidth
            value={assignedLawyers}
            onChange={assignValuesToSearchQueryBeforeSearch}
            renderInput={params => (
              <TextField
                {...params}
                style={{
                  border: `1px solid ${COLORS.FORM_INPUT_BORDER}`,
                  width: '100%',
                  height: '40px',
                  padding: '10px',
                }}
                fullWidth
                disabled={false}
              />
            )}
          />
        </FormControl>
      )}

      <SearchButton data-testid="search-button" onClick={handleSearch}>
        <SearchIcon />
      </SearchButton>
      <RefreshButton data-testid="refresh-button" onClick={handlerRefresh}>
        <RefreshIcon />
      </RefreshButton>
    </Container>
  );
};

export default injectIntl(SearchBar);
